import { api } from "@/lib/api";
import type {
  EquipmentArticle,
  EquipmentPiece,
  EquipmentPlace,
  WireTransfer,
} from "./oprema";

export type Bucket = "ispravno" | "osteceno" | "u_radioni";
export interface E2Document {
  id: string;
  broj: string;
  content_url: string;
  bez_potpisa: boolean;
  bez_fotke: boolean;
  potpisani?: string[];
}
export interface E2Result {
  id: string;
  dokument: E2Document;
  status: string;
  ponistivo_do: string;
  najam_id?: string;
}
export interface RentalBalance {
  id: string;
  artikl_id: string;
  mjesto_id: string;
  najam_id: string;
  ispravno: number;
  osteceno: number;
  u_radioni: number;
}
export interface Rental {
  id: string;
  dobavljac_id: string;
  broj_ugovora: string;
  datum_preuzimanja: string;
  povrat_do: string;
  u_mjesto_id: string;
  cijena_dan: string | null;
  cijena_mjesec: string | null;
  napomena: string | null;
  status: "otvoren" | "djelomicno_vracen" | "zatvoren";
  najmodavac: string;
  dani_u_najmu: number;
  dana_do_povrata: number;
  informativni_trosak: string | null;
  vani: number;
  stanje: RentalBalance[];
  komadi: EquipmentPiece[];
  u_prijevozu?: {
    selidba_id: string;
    artikl_id: string;
    komad_id: string | null;
    najam_id: string;
    kolicina: number;
    iz_mjesta_id: string;
    u_mjesto_id: string;
    iz_mjesta: string;
    u_mjesto: string;
  }[];
  dokumenti: E2Document[];
  produljenja: {
    id: string;
    stari_rok: string;
    novi_rok: string;
    ponisteno?: boolean;
    razlog: string;
    created_at: string;
  }[];
}
export type InventoryResolution =
  "trazi_dalje" | "nestalo" | "osteceno" | "pronadeno" | "doslo_s_drugog";
export interface InventoryItem {
  id: string;
  artikl_id: string;
  komad_id: string | null;
  najam_id: string | null;
  najmodavac: string | null;
  inventarni_broj: string | null;
  naziv: string;
  stanje: Bucket;
  ocekivano: number;
  izbrojano: number | null;
  razlika: number | null;
  rjesenje: InventoryResolution | null;
  razlog:
    | "manjak"
    | "krivo_upisano_ranije"
    | "drugo"
    | "pronadeno"
    | "doslo_s_drugog"
    | null;
  opis: string | null;
  iz_mjesta_id: string | null;
  dokumenti: string[];
  brojio_id?: string | null;
}
export interface Inventory {
  id: string;
  mjesto_id: string;
  vrsta: "periodicka" | "ad_hoc" | "zavrsna";
  status: "u_tijeku" | "ceka_upravu" | "zavrsena";
  zapoceo_id: string;
  zavrsio_id: string | null;
  odobrio_id: string | null;
  zavrseno_at: string | null;
  created_at: string;
  updated_at?: string;
  promijenjeno: boolean;
  stavke: InventoryItem[];
  dokument: E2Document | null;
  rezultat?: E2Result;
}
export interface Closure {
  id: string;
  mjesto_id: string;
  zatrazio_id: string;
  status: "ceka_upravu" | "zatvoreno" | "odbijeno" | "ponisteno";
  razlog: string | null;
}
export interface ClosureView {
  mjesto_id: string;
  gradiliste_id: string;
  aktivno: boolean;
  moze_zatvoriti: boolean;
  koraci: {
    kljuc: string;
    naziv: string;
    gotovo: boolean;
    akcija: string | null;
  }[];
  zahtjev: Closure | null;
  dokument: E2Document | null;
  rezultat?: E2Result;
}
export interface EquipmentRequest {
  id: string;
  mjesto_id: string;
  user_id: string;
  potrebno_do: string;
  napomena: string | null;
  status: "otvoren" | "poslano" | "ponisteno";
  selidba_id: string | null;
  created_at: string;
  stavke: { id: string; artikl_id: string; naziv: string; kolicina: number }[];
}
export interface Warning {
  vrsta: string;
  poruka: string;
  mjesto_id: string;
  najam_id?: string;
  boja: "warn" | "bad" | "neutral";
}
export interface ProofFiles {
  photos: File[];
  signature?: File | null;
  second?: File | null;
}

const root = "/oprema/e2";
async function get<T>(path: string, signal?: AbortSignal, params?: object) {
  return (await api.get<T>(`${root}${path}`, { signal, params })).data;
}
async function post<T>(path: string, data?: unknown) {
  return (await api.post<T>(`${root}${path}`, data)).data;
}
export function evidenceBody(data: unknown, files: ProofFiles): FormData {
  const form = new FormData();
  form.append("podaci", JSON.stringify(data));
  files.photos.forEach((file) => form.append("fotke", file));
  if (files.signature) form.append("potpis", files.signature);
  if (files.second) form.append("drugi_potpis", files.second);
  return form;
}
export const equipmentE2Api = {
  rentals: (signal?: AbortSignal) => get<Rental[]>("/najmovi", signal),
  rental: (id: string, signal?: AbortSignal) =>
    get<Rental>(`/najmovi/${id}`, signal),
  suppliers: (signal?: AbortSignal) =>
    get<{ id: string; naziv: string }[]>("/najmodavci", signal),
  receiveRental: (data: unknown, files: ProofFiles) =>
    post<E2Result>("/najmovi", evidenceBody(data, files)),
  returnRental: (id: string, data: unknown, files: ProofFiles) =>
    post<E2Result>(`/najmovi/${id}/povrat`, evidenceBody(data, files)),
  damageRental: (id: string, data: unknown, photos: File[]) =>
    post<E2Result>(`/najmovi/${id}/steta`, evidenceBody(data, { photos })),
  extendRental: (id: string, povrat_do: string, razlog: string) =>
    post<E2Result>(`/najmovi/${id}/produlji`, { povrat_do, razlog }),
  inventories: (place?: string, signal?: AbortSignal) =>
    get<Inventory[]>("/inventure", signal, { mjesto: place }),
  inventory: (id: string, signal?: AbortSignal) =>
    get<Inventory>(`/inventure/${id}`, signal),
  startInventory: (mjesto_id: string, vrsta: Inventory["vrsta"]) =>
    post<Inventory>("/inventure", { mjesto_id, vrsta }),
  countInventory: async (id: string, stavke: unknown[]) =>
    (await api.put<Inventory>(`${root}/inventure/${id}/brojanje`, { stavke }))
      .data,
  extraInventory: (id: string, data: unknown) =>
    post<Inventory>(`/inventure/${id}/stavke`, data),
  refreshInventory: (id: string) => post<Inventory>(`/inventure/${id}/osvjezi`),
  finishInventory: (id: string, potpis_id?: string) =>
    post<Inventory>(`/inventure/${id}/zavrsi`, {
      potpis_id: potpis_id ?? null,
    }),
  decideInventory: (id: string, odobri: boolean, razlog: string) =>
    post<Inventory>(`/inventure/${id}/odluka`, { odobri, razlog }),
  closure: (place: string, signal?: AbortSignal) =>
    get<ClosureView>(`/mjesta/${place}/zatvaranje`, signal),
  close: (place: string, razlog?: string) =>
    post<ClosureView>(`/mjesta/${place}/zatvaranje`, {
      razlog: razlog ?? null,
    }),
  decideClosure: (id: string, odobri: boolean, razlog: string) =>
    post<ClosureView>(`/zatvaranja/${id}/odluka`, { odobri, razlog }),
  requests: (place?: string, signal?: AbortSignal) =>
    get<EquipmentRequest[]>("/zahtjevi", signal, { mjesto: place }),
  request: (data: unknown) => post<EquipmentRequest>("/zahtjevi", data),
  cancelRequest: (id: string) =>
    post<EquipmentRequest>(`/zahtjevi/${id}/ponisti`),
  dispatch: (id: string, data: unknown, files: ProofFiles) =>
    post<WireTransfer>(`/zahtjevi/${id}/posalji`, evidenceBody(data, files)),
  warnings: (place?: string, signal?: AbortSignal) =>
    get<Warning[]>("/upozorenja", signal, { mjesto: place }),
  documents: (place?: string, signal?: AbortSignal) =>
    get<E2Document[]>("/dokumenti", signal, { mjesto: place }),
  document: async (id: string, signal?: AbortSignal) =>
    (
      await api.get<Blob>(`${root}/dokumenti/${id}`, {
        signal,
        responseType: "blob",
      })
    ).data,
  signed: (id: string, photo: File) =>
    post<{ dokument: E2Document }>(
      `/dokumenti/${id}/potpisani`,
      evidenceBody({}, { photos: [photo] }),
    ),
  undo: (id: string) => post<E2Result>(`/dokumenti/${id}/ponisti`),
  uploadPhotos: async (photos: File[]) =>
    (
      await api.post<{ dokumenti: string[] }>(
        "/oprema/datoteke",
        evidenceBody({}, { photos }),
      )
    ).data.dokumenti,
  uploadSignature: async (signature: File) =>
    (
      await api.post<{ potpis_id: string }>(
        "/oprema/potpisi",
        evidenceBody({}, { photos: [], signature }),
      )
    ).data.potpis_id,
  findPiece: async (label: string) =>
    (
      await api.get<{ id: string; artikl_id: string; najam_id: string | null }>(
        `/oprema/komadi/po-oznaci/${encodeURIComponent(label)}`,
      )
    ).data,
};
export interface EquipmentLookup {
  articles: EquipmentArticle[];
  places: EquipmentPlace[];
}
